import { MicroMoments } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  checkDefined,
  getEmptyContent,
  getGenericLabel,
  getImage,
  getLink,
  isDisplay,
  isError,
  isValidData,
  PEMicroMoment,
  UserSession
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { MultiplePersonalizationEngineResponse } from '../models';
import { LoyaltyMemberTierLevel } from '../models/api/tracker-details.interfaces';
import { StatusTrackerResponse } from '../models/api/tracker.interfaces';
import { TYPE } from '../models/overview.constants';
import { checkMicroMomentAndAdapt } from '../utils/micro-moments.utils';

export const getMicroMoments = (
  featureFlag: boolean | undefined,
  personalizationEngine: MultiplePersonalizationEngineResponse | undefined,
  tracker: StatusTrackerResponse | undefined,
  profile: UserSession | undefined,
  errors: (string | undefined)[]
): MicroMoments => {
  try {
    if (errors.filter((error) => error !== undefined).length) {
      return isError<MicroMoments>('', undefined, featureFlag);
    }
    return isValidData<
      MultiplePersonalizationEngineResponse,
      [
        UserSession | undefined,
        MultiplePersonalizationEngineResponse | undefined,
        StatusTrackerResponse | undefined,
        boolean | undefined
      ]
    >('firstName', profile, personalizationEngine, tracker, featureFlag)
      ? checkMicroMomentAndAdapt(
          personalizationEngine as MultiplePersonalizationEngineResponse,
          profile as UserSession,
          tracker as StatusTrackerResponse,
          featureFlag as boolean
        )
      : isDisplay<MicroMoments>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'MICRO_MOMENTS');
    return getEmptyContent<MicroMoments>('', [], true, true);
  }
};

export const adaptPEMicroMoment = (
  personalizationEngine: MultiplePersonalizationEngineResponse,
  profile: UserSession,
  tracker: StatusTrackerResponse,
  featureFlag: boolean
): MicroMoments => {
  const micromoment = personalizationEngine['ng-skymiles:micro-moments-v1']
    .parsys.micromoments as PEMicroMoment;
  const enrolledMonth: string = new Date(
    profile.enrollmentDate + 'T12:00:00Z'
  ).toLocaleString(TYPE.OPTIONS_DEFAULT, { month: TYPE.OPTIONS_MONTH });
  const enrolledYear: string = new Date(
    profile.enrollmentDate + 'T12:00:00Z'
  ).toLocaleString(TYPE.OPTIONS_DEFAULT, { year: TYPE.OPTIONS_YEAR });
  const anniversaryYears: number = new Date().getFullYear() - +enrolledYear;

  const result: MicroMoments = {
    id: micromoment.microMomentType,
    title: micromoment.title.replace(TYPE.NAME_PLACEHOLDER, profile.firstName),
    description: getMicromomentDescription(
      checkDefined(micromoment.description),
      profile.enrollmentDate,
      enrolledMonth,
      anniversaryYears,
      tracker
    ),
    logo: getImage(micromoment.logoImageUrl, micromoment.logoAltText),
    close: {
      path: micromoment.closePath,
      alt: micromoment.closeAltText
    },
    ariaLabel: { close: micromoment.ariaLabelClose },
    disabled: !featureFlag,
    loaded: true
  };

  if (micromoment.microMomentType === 'skymiles-anniversary')
    result.logo.label = getMicromomentLogoLabel(anniversaryYears);
  if ((micromoment.actionLabel, micromoment.actionUrl)) {
    result.actionId = micromoment.microMomentType + '-link';
    result.action = getLink(
      checkDefined(micromoment.actionLabel),
      checkDefined(micromoment.actionUrl)
    );
  }
  if ((micromoment.tagTitle, micromoment.tagSrc, micromoment.tagAlt)) {
    result.tag = {
      title: checkDefined(micromoment.tagTitle),
      image: getImage(
        checkDefined(micromoment.tagSrc),
        checkDefined(micromoment.tagAlt)
      )
    };
  }

  return result;
};

const getMicromomentDescription = (
  description: string,
  enrollmentDate: string,
  enrolledMonth: string,
  anniversaryYear: number,
  tracker: StatusTrackerResponse
): string => {
  const downgradeTierLevelDesc =
    getTierLevel(tracker.loyaltyMemberTierLevels) || '';
  return description
    .replace(TYPE.MONTH_PLACEHOLDER, enrolledMonth)
    .replace(TYPE.YEAR_PLACEHOLDER, enrollmentDate.split('-')[0])
    .replace(TYPE.X_PLACEHOLDER, anniversaryYear.toString())
    .replace(TYPE.MEDALLION_STATUS, downgradeTierLevelDesc);
};

const getMicromomentLogoLabel = (anniversaryYears: number) => {
  return getGenericLabel(
    anniversaryYears.toString(),
    anniversaryYears === 1 ? TYPE.YEAR_LABEL : TYPE.YEARS_LABEL
  );
};

export const getTierLevel = (
  tierLevels: LoyaltyMemberTierLevel[]
): string | undefined => {
  const memberTierLevel =
    tierLevels.find(
      (tierLevel) =>
        tierLevel.tierStatusDesc === TYPE.CURRENT &&
        tierLevel.tierLevelClassificatinCode === TYPE.TIER_CODE
    )?.tierNextPeriodDowngradedTierLevelDesc || '';
  return memberTierLevel;
};
